// src/pages/admin/admin-talleres.tsx
// REFACTORIZADO: Usa el hook useTaller para centralizar la lógica

import React, { useEffect, useState } from 'react';
import { deleteDoc, doc, getDoc, addDoc, collection, Timestamp, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import UserNav from '../../components/navbar/user-nav';

// Importar el hook y tipos desde useTaller
import { useTaller, Taller, TallerFilters } from '../../hooks/useTaller';

// Importar componentes de loading
import { LoadingMessage, ErrorState, EmptyState } from '../../components/loading-states';

// Helpers de borradores en Firestore
import { listarBorradoresFirestore, eliminarBorradorFirestore, DraftTallerDoc } from '../../utils/draftTaller';
import { useUser } from '../../context/UserContext';

// Modal de compartir evento
import CompartirTallerModal from '../../components/CompartirTallerModal';

const formatHaceCuanto = (millis: number): string => {
  const diff = Date.now() - millis;
  const min = Math.round(diff / 60000);
  if (min < 1) return 'hace unos segundos';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  return `hace ${d} día${d === 1 ? '' : 's'}`;
};

export default function AdminTalleres() {
  const navigate = useNavigate();
  const { user } = useUser();

  // ========== USAR EL HOOK useTaller ==========
  // Esto reemplaza ~90 líneas de lógica duplicada (interfaces, calcularEstado, fetch, filtrado)
  const {
    talleres,
    loading,
    error,
    fetchAllTalleres,
    filtrarTalleres,
    formatearFecha
  } = useTaller();

  // Estados locales (solo UI)
  const [menuAbierto, setMenuAbierto] = useState<string | null>(null);
  const [filtros, setFiltros] = useState<TallerFilters>({
    nombre: '',
    mes: '',
    costo: 'todos',
    creditos: 'todos',
    estado: 'todos'
  });

  // Cargar talleres al montar el componente
  useEffect(() => {
    fetchAllTalleres(true); // true = incluir conteo de inscritos
  }, [fetchAllTalleres]);

  // Borradores en progreso (de cualquier usuario del equipo)
  const [borradores, setBorradores] = useState<DraftTallerDoc[]>([]);
  const [borradoresLoading, setBorradoresLoading] = useState(false);

  const cargarBorradores = async () => {
    setBorradoresLoading(true);
    try {
      const lista = await listarBorradoresFirestore();
      // Más recientes primero
      lista.sort((a, b) => (b.savedAtMillis || 0) - (a.savedAtMillis || 0));
      setBorradores(lista);
    } catch (err) {
      console.warn('No se pudieron cargar los borradores:', err);
    } finally {
      setBorradoresLoading(false);
    }
  };

  useEffect(() => {
    cargarBorradores();
  }, []);

  const handleDescartarBorrador = async (uid: string, nombreEvento: string) => {
    const result = await Swal.fire({
      title: '¿Descartar borrador?',
      html: `Se eliminará el borrador <strong>${nombreEvento}</strong>. Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, descartar',
      cancelButtonText: 'Cancelar'
    });
    if (!result.isConfirmed) return;
    try {
      await eliminarBorradorFirestore(uid);
      cargarBorradores();
    } catch (err) {
      Swal.fire('Error', 'No se pudo eliminar el borrador.', 'error');
    }
  };

  // Aplicar filtros usando la función del hook
  const talleresFiltrados = filtrarTalleres(talleres, filtros);

  // Cerrar el menú al hacer clic fuera de él
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.position-relative')) {
        setMenuAbierto(null);
      }
    };

    if (menuAbierto) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [menuAbierto]);

  // Handlers de acciones
  const handleDelete = async (id: string) => {
    // Contar inscripciones vinculadas antes de confirmar
    let internos = 0;
    let externos = 0;
    let inscripcionesDocs: { ref: any }[] = [];
    try {
      const [snapInt, snapExt] = await Promise.all([
        getDocs(query(collection(db, 'inscripcion'), where('taller_doc_id', '==', id))),
        getDocs(query(collection(db, 'externo_inscripcion'), where('taller_doc_id', '==', id)))
      ]);
      internos = snapInt.size;
      externos = snapExt.size;
      inscripcionesDocs = [
        ...snapInt.docs.map(d => ({ ref: doc(db, 'inscripcion', d.id) })),
        ...snapExt.docs.map(d => ({ ref: doc(db, 'externo_inscripcion', d.id) }))
      ];
    } catch (err) {
      Swal.fire('Error', 'No se pudieron verificar las inscripciones del taller.', 'error');
      return;
    }

    const total = internos + externos;
    const advertencia = total > 0
      ? `<div class="alert alert-warning text-start mb-0">
           <strong>Este taller tiene ${total} inscripción(es):</strong>
           <ul class="mb-0">
             <li>${internos} interna(s) (afiliados/asociados)</li>
             <li>${externos} externa(s) (público)</li>
           </ul>
           <p class="mt-2 mb-0">Todas serán eliminadas junto con el taller.</p>
         </div>`
      : '<p>No hay inscripciones asociadas.</p>';

    const result = await Swal.fire({
      title: '¿Eliminar taller?',
      html: `${advertencia}<p class="mt-3 text-danger small">Esta acción no se puede deshacer.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: total > 0 ? `Sí, eliminar taller y ${total} inscripción(es)` : 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    try {
      // writeBatch: borra taller + todas sus inscripciones en una sola operación atómica
      // (límite 500 writes por batch, suficiente para talleres típicos)
      const batch = writeBatch(db);
      batch.delete(doc(db, 'taller', id));
      inscripcionesDocs.forEach(({ ref }) => batch.delete(ref));
      await batch.commit();

      fetchAllTalleres(true);
      Swal.fire(
        'Eliminado',
        total > 0
          ? `Taller e ${total} inscripción(es) eliminadas.`
          : 'El taller ha sido eliminado.',
        'success'
      );
    } catch (err) {
      console.error('Error al eliminar taller:', err);
      Swal.fire('Error', 'No se pudo eliminar el taller.', 'error');
    }
  };

  const [duplicando, setDuplicando] = useState(false);
  const [compartirTaller, setCompartirTaller] = useState<Taller | null>(null);

  // Genera un nombre limpio para el duplicado evitando "(Copia) (Copia) (Copia)".
  // Si el nombre ya termina en "(Copia)" o "(Copia N)", incrementa el contador.
  const generarNombreCopia = (nombreOriginal: string): string => {
    const base = nombreOriginal || 'taller';
    const matchN = base.match(/^(.*?)\s*\(Copia\s*(\d+)\)\s*$/i);
    if (matchN) {
      const n = parseInt(matchN[2], 10) + 1;
      return `${matchN[1]} (Copia ${n})`;
    }
    const match1 = base.match(/^(.*?)\s*\(Copia\)\s*$/i);
    if (match1) {
      return `${match1[1]} (Copia 2)`;
    }
    return `${base} (Copia)`;
  };

  const handleDuplicate = async (id: string) => {
    if (duplicando) return; // evita doble-click

    const result = await Swal.fire({
      title: 'Duplicar Taller',
      html: `
        <p>Se creará una copia del taller con:</p>
        <ul class="text-start">
          <li>Mismo contenido y configuración</li>
          <li>Sin inscritos ni asistencias</li>
          <li>Conserva la fecha original (puedes cambiarla)</li>
        </ul>
        <p class="text-muted small">El nuevo taller se abrirá para edición.</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, duplicar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    setDuplicando(true);
    try {
      const tallerRef = doc(db, 'taller', id);
      const tallerSnap = await getDoc(tallerRef);

      if (!tallerSnap.exists()) {
        Swal.fire('Error', 'No se encontró el taller original.', 'error');
        return;
      }

      const tallerOriginal = tallerSnap.data();

      const generarNuevoId = (nombre: string): string => {
        const letras = nombre.replace(/[^a-zA-Z]/g, '').toLowerCase().slice(0, 3) || 'evt';
        const now = new Date();
        const dia = String(now.getDate()).padStart(2, '0');
        const mes = String(now.getMonth() + 1).padStart(2, '0');
        const anio = String(now.getFullYear()).slice(-2);
        const random = Math.floor(Math.random() * 100).toString().padStart(2, '0');
        return `${letras}${dia}${mes}${anio}${random}`;
      };

      // Conserva fecha original; solo renombra y marca como nuevo.
      const nuevoTaller = {
        ...tallerOriginal,
        taller_id: generarNuevoId(tallerOriginal.nombre || 'taller'),
        nombre: generarNombreCopia(tallerOriginal.nombre || 'Taller'),
        fechaCreacion: Timestamp.fromDate(new Date()),
        activo: true,
        estado: 'proximo'
      };

      const nuevoDocRef = await addDoc(collection(db, 'taller'), nuevoTaller);

      await Swal.fire({
        icon: 'success',
        title: 'Taller Duplicado',
        html: `
          <p>Se creó una copia con el nombre <strong>${nuevoTaller.nombre}</strong>.</p>
          <p class="text-muted small">Ajusta la fecha y los detalles que necesites.</p>
        `,
        confirmButtonText: 'Ir a Editar',
        confirmButtonColor: '#28a745'
      });

      navigate(`/edit-taller/${nuevoDocRef.id}`);
    } catch (err) {
      console.error('Error al duplicar taller:', err);
      Swal.fire('Error', 'No se pudo duplicar el taller.', 'error');
    } finally {
      setDuplicando(false);
    }
  };

  const limpiarFiltros = () => {
    setFiltros({
      nombre: '',
      mes: '',
      costo: 'todos',
      creditos: 'todos',
      estado: 'todos'
    });
  };

  // Actualizar un filtro específico
  const actualizarFiltro = (campo: keyof TallerFilters, valor: string) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  // ========== RENDER ==========

  // Estado de carga - usando componente LoadingMessage
  if (loading) {
    return (
      <>
        <UserNav />
        <div className="container mt-5">
          <LoadingMessage message="Cargando talleres..." subMessage="Por favor espera" />
        </div>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  // Estado de error - usando componente ErrorState
  if (error) {
    return (
      <>
        <UserNav />
        <div className="container mt-5">
          <ErrorState
            title="Error al cargar talleres"
            message={error}
            onRetry={() => fetchAllTalleres(true)}
          />
        </div>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  return (
    <>
      <UserNav />
      <div className="container mt-5">
        <h1 className="text-center mb-4">Administración de Talleres</h1>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <button className="btn btn-success" onClick={() => navigate('/admin-create-taller')}>
            + Agregar Taller
          </button>
        </div>

        {/* Borradores en progreso (Firestore) */}
        {borradores.length > 0 && (
          <div className="card mb-4 border-warning">
            <div className="card-header bg-warning bg-opacity-25 d-flex align-items-center justify-content-between">
              <h6 className="mb-0">
                <i className="fa-solid fa-pen-clip me-2"></i>
                Borradores en progreso ({borradores.length})
              </h6>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={cargarBorradores}
                disabled={borradoresLoading}
                title="Refrescar lista"
              >
                <i className="fa-solid fa-rotate"></i>
              </button>
            </div>
            <div className="table-responsive">
              <table className="table table-sm mb-0" aria-label="Borradores de talleres en progreso">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Evento (provisional)</th>
                    <th scope="col">Creado por</th>
                    <th scope="col">Última edición</th>
                    <th scope="col" className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {borradores.map(b => {
                    const esMio = user?.uid === b.user_uid;
                    return (
                      <tr key={b.user_uid}>
                        <td>{b.nombre_evento || '(sin nombre)'}</td>
                        <td>
                          {b.user_nombre || b.user_correo || b.user_uid}
                          {esMio && <span className="badge bg-info ms-2">Tú</span>}
                        </td>
                        <td>{formatHaceCuanto(b.savedAtMillis)}</td>
                        <td className="text-end">
                          {esMio ? (
                            <button
                              className="btn btn-sm btn-warning me-2"
                              onClick={() => navigate('/admin-create-taller')}
                            >
                              <i className="fa-solid fa-arrow-right me-1"></i>
                              Continuar
                            </button>
                          ) : (
                            <span className="text-muted small me-2">
                              <i className="fa-solid fa-lock me-1"></i>
                              Solo el autor puede editarlo
                            </span>
                          )}
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDescartarBorrador(b.user_uid, b.nombre_evento || '(sin nombre)')}
                            title="Descartar este borrador"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="card mb-4 p-3 shadow-sm">
          <h5 className="mb-3">
            <i className="fa-solid fa-filter me-2"></i>Filtros de Búsqueda
          </h5>
          <div className="row g-3">
            <div className="col-md-6 col-lg-3">
              <label className="form-label small fw-bold">Nombre del Taller</label>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por nombre..."
                value={filtros.nombre || ''}
                onChange={(e) => actualizarFiltro('nombre', e.target.value)}
              />
            </div>

            <div className="col-md-6 col-lg-3">
              <label className="form-label small fw-bold">Mes</label>
              <input
                type="month"
                className="form-control"
                value={filtros.mes || ''}
                onChange={(e) => actualizarFiltro('mes', e.target.value)}
              />
            </div>

            <div className="col-md-6 col-lg-3">
              <label className="form-label small fw-bold">Tipo de Costo</label>
              <select
                className="form-select"
                value={filtros.costo || 'todos'}
                onChange={(e) => actualizarFiltro('costo', e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="gratuito">Gratuito</option>
                <option value="pago">Pago</option>
              </select>
            </div>

            <div className="col-md-6 col-lg-2">
              <label className="form-label small fw-bold">Créditos</label>
              <select
                className="form-select"
                value={filtros.creditos || 'todos'}
                onChange={(e) => actualizarFiltro('creditos', e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="con_creditos">Con Créditos</option>
                <option value="sin_creditos">Sin Créditos</option>
              </select>
            </div>

            <div className="col-md-6 col-lg-2">
              <label className="form-label small fw-bold">Estado</label>
              <select
                className="form-select"
                value={filtros.estado || 'todos'}
                onChange={(e) => actualizarFiltro('estado', e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="proximo">Próximo</option>
                <option value="en_curso">En Curso</option>
                <option value="finalizado">Finalizado</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
          </div>

          <div className="mt-3 text-end">
            <button className="btn btn-outline-secondary btn-sm" onClick={limpiarFiltros}>
              <i className="fa-solid fa-times me-1"></i>Limpiar Filtros
            </button>
            <span className="ms-3 text-muted small">
              Mostrando {talleresFiltrados.length} de {talleres.length} talleres
            </span>
          </div>
        </div>

        {/* Estado vacío - usando componente EmptyState */}
        {talleres.length === 0 ? (
          <EmptyState
            icon="fa-solid fa-chalkboard-user"
            title="No hay talleres registrados"
            message="Comienza creando tu primer taller"
            actionText="Crear Taller"
            onAction={() => navigate('/admin-create-taller')}
          />
        ) : talleresFiltrados.length === 0 ? (
          <div className="alert alert-info">
            <i className="fa-solid fa-info-circle me-2"></i>
            No se encontraron talleres con los filtros aplicados.
          </div>
        ) : (
          <div className="table-responsive">
            <table aria-label="Tabla de Admin Talleres" className="table table-bordered">
              <thead className="table-light">
                <tr>
                  <th scope="col">Nombre</th>
                  <th scope="col">Fecha</th>
                  <th scope="col">Horario</th>
                  <th scope="col">Lugar</th>
                  <th scope="col">Organizador</th>
                  <th scope="col">Cupo</th>
                  <th scope="col">Inscritos</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {talleresFiltrados.map((taller: Taller) => (
                  <tr key={taller.id}>
                    <td>{taller.nombre}</td>
                    <td>
                      {taller.fecha?.toDate?.().toLocaleDateString('es-MX') ||
                       taller.fechaInicio?.toDate?.().toLocaleDateString('es-MX') || 'N/A'}
                    </td>
                    <td>
                      {taller.horaInicio} - {taller.horaTermino}
                    </td>
                    <td>{taller.lugar}</td>
                    <td>{taller.organizadorPrincipal || taller.expositor}</td>
                    <td>{taller.cupoMaximo || taller.cupo}</td>
                    <td>
                      <span className="badge bg-info">
                        {taller.inscritos || 0}
                      </span>
                    </td>
                    <td>
                      {taller.estadoCalculado === 'proximo' && (
                        <span className="badge bg-primary">Próximo</span>
                      )}
                      {taller.estadoCalculado === 'en_curso' && (
                        <span className="badge bg-success">En Curso</span>
                      )}
                      {taller.estadoCalculado === 'finalizado' && (
                        <span className="badge bg-secondary">Finalizado</span>
                      )}
                      {taller.estadoCalculado === 'cancelado' && (
                        <span className="badge bg-danger">Cancelado</span>
                      )}
                      {taller.estadoCalculado === 'sin_fecha' && (
                        <span className="badge bg-warning text-dark">Sin Fecha</span>
                      )}
                    </td>
                    <td className="position-relative">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setMenuAbierto(menuAbierto === taller.id ? null : taller.id || null)}
                        aria-label="Abrir menú de acciones"
                      >
                        <i className="fa-solid fa-gear" aria-hidden="true"></i>
                      </button>

                      {menuAbierto === taller.id && (
                        <div
                          className="position-absolute end-0 mt-1 bg-white border rounded shadow-sm"
                          style={{ zIndex: 1000, minWidth: '180px' }}
                        >
                          <div className="list-group list-group-flush">
                            <button
                              className="list-group-item list-group-item-action d-flex align-items-center"
                              onClick={() => {
                                navigate(`/ver-taller-ind/${taller.id}`);
                                setMenuAbierto(null);
                              }}
                            >
                              <i className="fa-solid fa-eye me-2" aria-hidden="true"></i>
                              Ver Detalles
                            </button>
                            <button
                              className="list-group-item list-group-item-action d-flex align-items-center"
                              onClick={() => {
                                navigate(`/edit-taller/${taller.id}`);
                                setMenuAbierto(null);
                              }}
                            >
                              <i className="fa-solid fa-pen-to-square me-2" aria-hidden="true"></i>
                              Editar
                            </button>
                            <button
                              className="list-group-item list-group-item-action d-flex align-items-center text-primary"
                              onClick={() => {
                                setCompartirTaller(taller);
                                setMenuAbierto(null);
                              }}
                            >
                              <i className="fa-solid fa-share-nodes me-2" aria-hidden="true"></i>
                              Compartir
                            </button>
                            <button
                              className="list-group-item list-group-item-action d-flex align-items-center text-success"
                              disabled={duplicando}
                              onClick={() => {
                                handleDuplicate(taller.id as string);
                                setMenuAbierto(null);
                              }}
                            >
                              <i className="fa-solid fa-copy me-2" aria-hidden="true"></i>
                              {duplicando ? 'Duplicando...' : 'Duplicar'}
                            </button>
                            <button
                              className="list-group-item list-group-item-action d-flex align-items-center text-danger"
                              onClick={() => {
                                handleDelete(taller.id as string);
                                setMenuAbierto(null);
                              }}
                            >
                              <i className="fa-solid fa-trash me-2" aria-hidden="true"></i>
                              Eliminar
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {compartirTaller && (
        <CompartirTallerModal
          visible={!!compartirTaller}
          onClose={() => setCompartirTaller(null)}
          tallerId={compartirTaller.id || ''}
          nombre={compartirTaller.nombre || ''}
          fechaTexto={
            compartirTaller.fecha?.toDate?.().toLocaleDateString('es-MX', {
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
            }) || ''
          }
          estado={compartirTaller.estadoCalculado}
        />
      )}

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
